import { navigationMenuItemFragment } from '@/fragments/NavigationMenu'
import flatListToHierarchical from '@/utilities/flatListToHierarchical'
import { gql, useQuery } from '@apollo/client'
import React, { createContext, useContext } from 'react'

export const SITE_DATA_QUERY = gql`
  ${navigationMenuItemFragment}
  query GetSiteSettings {
    generalSettings {
      title
      description
    }
    headerMenuItems: menuItems(first: 100, where: { location: PRIMARY }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: FOOTER }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`

interface MenuItem {
  id: string
  label: string
  path: string
  children: MenuItem[]
  [key: string]: unknown
}

interface Banner {
  enabled: boolean
  message: string
  linkText: string
  linkUrl: string
}

interface SiteDataContextType {
  settings: {
    title: string
    description: string
  }
  headerMenuItems: MenuItem[]
  footerMenuItems: MenuItem[]
  banner: Banner | null
}

interface SiteDataQueryResult {
  generalSettings: {
    title: string
    description: string
  }
  headerMenuItems: {
    nodes: MenuItem[]
  }
  footerMenuItems: {
    nodes: MenuItem[]
  }
  siteBannerOptions?: {
    siteBanner?: Partial<Banner> | null
  } | null
}

// Update context creation with proper type
const SiteDataContext = createContext<SiteDataContextType | null>(null)

interface SiteDataProviderProps {
  children: React.ReactNode
  initialData?: unknown
}

function isCompleteSiteData(value: unknown): value is SiteDataQueryResult {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return Boolean(v.generalSettings && v.headerMenuItems && v.footerMenuItems)
}

// Map the optional banner options field to a complete Banner, or null when the
// field is absent/partial. Keeps banner failures from affecting site data.
function normalizeBanner(raw: Partial<Banner> | null | undefined): Banner | null {
  if (!raw) return null
  return {
    enabled: Boolean(raw.enabled),
    message: raw.message ?? '',
    linkText: raw.linkText ?? '',
    linkUrl: raw.linkUrl ?? ''
  }
}

export function SiteDataProvider({ children, initialData }: SiteDataProviderProps) {
  const validInitialData = isCompleteSiteData(initialData) ? initialData : undefined

  const { data } = useQuery<SiteDataQueryResult>(SITE_DATA_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !!validInitialData
  })

  const resolved = validInitialData ?? data

  const settings = resolved?.generalSettings ?? {
    title: 'St. Louis Web Design Company | Web Development Agency',
    description:
      'Integrity is a leading web consulting firm delivering omnichannel marketing, UX/UI design, media, experiential/events, and technology solutions to clients globally for over 25 years.'
  }

  const headerMenuItems = flatListToHierarchical(resolved?.headerMenuItems?.nodes ?? [])
  const footerMenuItems = flatListToHierarchical(resolved?.footerMenuItems?.nodes ?? [])
  const banner = normalizeBanner(resolved?.siteBannerOptions?.siteBanner)

  const contextValue = {
    settings,
    headerMenuItems,
    footerMenuItems,
    banner
  }

  return <SiteDataContext.Provider value={contextValue}>{children}</SiteDataContext.Provider>
}

const FALLBACK_SITE_DATA: SiteDataContextType = {
  settings: {
    title: 'St. Louis Web Design Company | Web Development Agency',
    description:
      'Integrity is a leading web consulting firm delivering omnichannel marketing, UX/UI design, media, experiential/events, and technology solutions to clients globally for over 25 years.'
  },
  headerMenuItems: [],
  footerMenuItems: [],
  banner: null
}

export function useSiteData() {
  const context = useContext(SiteDataContext)
  return context ?? FALLBACK_SITE_DATA
}
